import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, BookOpen, Users, ArrowRight, ArrowUpRight, CheckCircle2, FileText, Compass, HeartPulse, Building2, Layers, Award } from "lucide-react";
import { Section, Eyebrow, H1, H2, Lead, ButtonPrimary, ButtonGhost, GlassCard } from "../components/ui-kit";

const SELECTION_CRITERIA = [
  {
    icon: Compass,
    title: "Visionary Leadership",
    body: "A principal or board willing to champion AVGC as a complementary, structured career pathway alongside traditional academics.",
  },
  {
    icon: Building2,
    title: "Infrastructure Readiness",
    body: "An existing or newly provisioned Content Creator Lab (CCL), or a clear plan to operationalize one within 90 days.",
  },
  {
    icon: HeartPulse,
    title: "Commitment to Student Wellness",
    body: "Willingness to adopt ergonomics designed to align with ISO 45001 principles, screen-time governance, and parental reporting protocols.",
  },
  {
    icon: Layers,
    title: "Pedagogical Discipline",
    body: "An academic culture that treats AVGC as a structured, outcome-driven discipline — not as recreation.",
  },
  {
    icon: Award,
    title: "Commitment to Outcomes",
    body: "Readiness to track and publish quarterly Digital Competency Reports with curriculum compliance mapping.",
  },
  {
    icon: ShieldCheck,
    title: "Governance Posture",
    body: "Alignment with DPDP Act, POCSO-compliant trainer onboarding, and transparent procurement standards.",
  },
];

export default function PrincipalsCorner() {
  return (
    <div>
      <Section className="!pt-16">
        <Eyebrow>Principal's Corner</Eyebrow>
        <H1>Why eSports is the <span className="text-gradient-orange">new STEM</span>.</H1>
        <Lead className="mt-6">
          A dedicated portal for school boards, principals, and procurement officers. Built to answer the hard questions before they're asked.
        </Lead>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/contact?type=school" data-testid="pc-cta-empanel">
            <ButtonPrimary>Empanel Your School <ArrowRight className="w-4 h-4" /></ButtonPrimary>
          </Link>
          <Link to="/partnership" data-testid="pc-cta-pricing">
            <ButtonGhost>View Partnership Roadmap <ArrowRight className="w-4 h-4" /></ButtonGhost>
          </Link>
        </div>
      </Section>

      <Section className="!pt-0">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: BookOpen, t: "Pedagogy-First", d: "A 12-month Pro-Path syllabus with curriculum compliance mapping, taught by certified coaches — not streamers." },
            { icon: ShieldCheck, t: "Wellness-Safeguarded", d: "ISO 45001 aligned SOPs, screen-time caps, parental dashboards, ergo audits." },
            { icon: Users, t: "Outcome-Verifiable", d: "Every student graduates with a Perforange completion certificate (with curriculum compliance mapping) and a Perforange Score." },
          ].map((x, i) => {
            const Icon = x.icon;
            return (
              <GlassCard key={i} className="hover:border-[#FF8C00]/40 transition-all" data-testid={`pc-pillar-${i}`}>
                <Icon className="w-7 h-7 text-[#FF8C00]" />
                <h3 className="mt-5 font-display font-black text-xl text-white">{x.t}</h3>
                <p className="mt-2 text-sm text-[#A1A1AA]">{x.d}</p>
              </GlassCard>
            );
          })}
        </div>
      </Section>

      <Section className="!pt-0">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <Eyebrow color="#00FFD0">Safety & Compliance</Eyebrow>
            <H2>We sweat the small print.</H2>
            <Lead className="mt-4">
              Each lab undergoes a 42-point safety audit. Every SOP maps to a national or international standard.
            </Lead>
          </div>
          <div className="md:col-span-7 space-y-3">
            {[
              "ISO 45001 Occupational Health & Safety alignment",
              "Curriculum compliance mapping for Grades 6–12",
              "POCSO-compliant trainer background verification",
              "Digital wellbeing SOPs (screen-time, 20-20-20, posture)",
              "Parental consent workflows and transparent reporting",
              "Electrical, fire, and PC-lab infrastructure audits",
              "DPDP Act-compliant student data handling",
            ].map((b, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl glass">
                <CheckCircle2 className="w-5 h-5 text-[#00FFD0] shrink-0 mt-0.5" />
                <p className="text-sm text-[#D4D4D8]">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* The Institutional Standard — replaces Testimonials */}
      <Section className="!pt-0" id="institutional-standard">
        <div className="max-w-3xl mb-12">
          <Eyebrow>The Institutional Standard</Eyebrow>
          <H2>Beyond the Screen: A Commitment to Institutional Excellence.</H2>
          <Lead className="mt-4">
            For school leaders weighing the AVGC mandate, the question is no longer whether to act — it is who to act with. The pages below are written for that decision.
          </Lead>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* LEFT — Letter of Intent / Principal's Brief */}
          <div className="lg:col-span-7">
            <div
              className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-[#101015] via-[#0C0C10] to-[#0A0A0C] p-8 md:p-12 overflow-hidden"
              data-testid="principal-brief-loi"
            >
              {/* Letterhead */}
              <div className="flex items-center justify-between border-b border-white/10 pb-5 mb-7">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-[#FF8C00]/10 border border-[#FF8C00]/40 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-[#FF8C00]" />
                  </div>
                  <div>
                    <p className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF8C00]">Principal's Brief · 2026</p>
                    <p className="font-display font-black text-white text-sm">Founder's Message · Letter of Intent</p>
                  </div>
                </div>
                <div className="hidden sm:flex flex-col items-end text-[10px] font-mono-tech uppercase tracking-widest text-[#6a6a70]">
                  <span>Reference</span>
                  <span className="text-[#A1A1AA]">PFR / 2026 / LOI / 015</span>
                </div>
              </div>

              {/* Watermark */}
              <p className="absolute right-6 bottom-6 font-display font-black text-7xl md:text-8xl text-white/[0.02] select-none pointer-events-none leading-none">
                LOI
              </p>

              <div className="relative space-y-5 text-[#D4D4D8] leading-relaxed">
                <p className="text-base md:text-[17px]">
                  To the Principals, Trustees, and Boards of India's <span className="text-white font-bold">Model Schools</span>,
                </p>
                <p>
                  Every generation of school leadership inherits one defining question: <span className="text-white">how to modernize without compromising rigor.</span> The Union Budget 2026 AVGC mandate has placed that question on every principal's desk — to be answered within a single academic cycle.
                </p>
                <p>
                  We recognize the dilemma. The pressure to embrace gaming and content creation as legitimate career pathways collides with a justified concern: <em className="text-[#FF8C00] not-italic">distraction, dilution, and a lack of regulation</em>. Most vendors in this space sell hardware, gameplay, or hype. None of those are appropriate for a school.
                </p>
                <p>
                  <span className="text-white font-bold">Perforange is not a gaming vendor.</span> We are an Operational Partner. We provide the governance layer that turns raw gaming infrastructure into a <span className="text-gradient-orange font-bold">disciplined, structured career pathway</span> — pedagogy first, wellness embedded, outcomes verifiable.
                </p>
                <p>
                  Our framework is designed to be <span className="text-white font-bold">complementary to traditional academics</span>, not a replacement. Every Perforange-empaneled lab operates under structured screen-time caps, parental dashboards, ergonomics audits, and a curriculum that reinforces — rather than distracts from — core academic outcomes.
                </p>
                <p>
                  The schools that sign during this incubator phase will not just adopt a product. They will help <span className="text-white">define the national standard</span> for AVGC education in Indian schools.
                </p>

                <div className="pt-6 mt-2 border-t border-white/10 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF8C00] to-[#FF5A00] flex items-center justify-center text-[#0A0A0C] font-black">KM</div>
                  <div>
                    <p className="font-display font-black text-white">Kinjal Mukherjee</p>
                    <p className="text-xs uppercase tracking-widest text-[#A1A1AA] mt-0.5">Founder & CEO · Perforange</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Pilot Selection / Compliance Grid */}
          <div className="lg:col-span-5 space-y-6" data-testid="pilot-selection-block">
            <div className="rounded-2xl border border-[#FF8C00]/30 bg-gradient-to-br from-[#1a1208] via-[#0A0A0C] to-[#0C0C10] p-7 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#FF8C00]/15 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF8C00]">2026 Pilot Cohort</p>
                  <span className="px-2.5 py-1 rounded-full bg-[#FF8C00] text-[#0A0A0C] text-[10px] font-mono-tech font-black uppercase tracking-widest">
                    15 Slots
                  </span>
                </div>
                <h3 className="mt-3 font-display font-black text-2xl text-white leading-tight">Selection Criteria</h3>
                <p className="mt-3 text-sm text-[#D4D4D8] leading-relaxed">
                  We are currently reserving <span className="text-white font-bold">15 slots</span> for Model Schools to define the national standard before commercial rollout. Selection is consultative, not transactional.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="selection-criteria-grid">
              {SELECTION_CRITERIA.map((c, i) => {
                const Icon = c.icon;
                return (
                  <div
                    key={i}
                    data-testid={`criteria-${i}`}
                    className="rounded-xl border border-white/8 bg-[#0C0C10] p-4 hover:border-[#FF8C00]/40 hover:shadow-[0_0_30px_rgba(255,140,0,0.08)] transition-all"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-[#FF8C00]/10 border border-[#FF8C00]/30 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-[#FF8C00]" />
                      </div>
                      <p className="font-display font-black text-sm text-white leading-tight">{c.title}</p>
                    </div>
                    <p className="mt-2.5 text-xs text-[#A1A1AA] leading-relaxed">{c.body}</p>
                  </div>
                );
              })}
            </div>

            <Link to="/partnership" data-testid="apply-pilot-cta" className="block">
              <button className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#FF8C00] text-[#0A0A0C] font-black text-sm uppercase tracking-widest hover:bg-[#FFA333] hover:shadow-[0_0_40px_rgba(255,140,0,0.55)] transition-all">
                Apply for Pilot Selection <ArrowUpRight className="w-5 h-5" />
              </button>
            </Link>
            <p className="text-[11px] text-[#6a6a70] text-center px-4">
              Application is consultative. A partnerships lead will respond within 48 hours.
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
}